import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import { includes, debounce } from 'lodash'
import { FormattedMessage, useIntl } from 'react-intl'
import { useField } from 'formik'
import * as R from 'ramda'
import { FilePlus } from 'lucide-react'
import { INSTAGRAM_VALUE } from 'app/constants/channel'
import {
  useSocialMediaSetting,
  checkErrorRatio,
} from '../socials/useSocialMediaSetting'
import ImageGalleryDialog from './ImageGalleryDialog'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import { SECONDARY, CLOSE } from 'app/constants/button'
import { Info } from '@material-ui/icons'
import AttachmentImage from './attachmentImage'
import clsx from 'clsx'
import MessageAlert from 'app/components/MessageAlert'
import { useDispatch } from 'react-redux'
import {
  validateImageCount,
  createTempImage,
  generateUploadId,
  createUploadIdMap,
  createProgressHandlerFactory,
  replaceTempImage,
  cleanupFailedUploads,
  getFilesFromEvent,
  shouldShowMoreButton,
  shouldShowImage,
  getRemainingImagesCount,
} from './attachmentsHelpers'
import { useResponsiveDisplayLimit } from './useResponsiveDisplayLimit'
import { isImageLoading, getNonLoadingImagesCount } from './attachmentsHelpers'

export default (props) => {
  const {
    formik,
    isPreview = false,
    previewImages = [],
    onLoadingStateChange,
    onSyncedImageDelete,
    onDeleteAll,
  } = props

  const dispatch = useDispatch()

  const { formatMessage } = useIntl()
  const fileUpload = useRef(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)
  const [hasUserClosedDialog, setHasUserClosedDialog] = useState(false)
  const [hasRatioError, setHasRatioError] = useState(false)

  const [field, meta, helpers] = useField('mediaSocials')
  const { value } = field
  const formValue = value || []
  const { setValue, setTouched } = helpers

  const images = isPreview ? previewImages || [] : formValue

  const sortedImages = useMemo(() => {
    if (!images || images.length === 0) return images
    const selectedImage = R.find(R.prop('isSelected'), images)
    if (!selectedImage) return images
    const otherImages = R.reject(R.prop('isSelected'), images)
    return [selectedImage, ...otherImages]
  }, [images])

  const containerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(null)
  const displayLimit = useResponsiveDisplayLimit(containerWidth)
  const previousSelectedImageIdRef = useRef(null)

  const {
    state: { image, isLoading, isStateError, errorMessage },
    uploadImage,
    selectImage,
    deleteImage,
    onCloseErrorDialog,
  } = useSocialMediaSetting(formatMessage)

  const loadingCount = useMemo(
    () => R.compose(R.length, R.filter(isImageLoading))(images) || 0,
    [images],
  )
  const loadedCount = useMemo(() => getNonLoadingImagesCount(images), [images])

  useEffect(() => {
    if (onLoadingStateChange && !isPreview) {
      onLoadingStateChange(loadingCount > 0)
    }
  }, [loadingCount, onLoadingStateChange, isPreview])

  useEffect(() => {
    const ratioError = formatMessage({
      id: 'compose.dialog.attachments.instagram.ratioError',
    })

    const hasInstagramChannel =
      formik?.values?.channels?.channelIds?.includes(INSTAGRAM_VALUE)
    const shouldCheckRatio =
      !isPreview &&
      loadingCount === 0 &&
      images?.length > 0 &&
      hasInstagramChannel

    if (!shouldCheckRatio) {
      setHasRatioError(false)
      formik?.setFieldError?.('mediaSocials', undefined)
      previousSelectedImageIdRef.current = null
      return
    }

    if (hasRatioError && !images?.length) {
      setHasRatioError(false)
      formik?.setFieldError?.('mediaSocials', undefined)
      previousSelectedImageIdRef.current = null
      return
    }

    const selectedImage = R.find(R.prop('isSelected'), images)
    const hasValidSelectedImage = !!selectedImage?.url

    if (hasValidSelectedImage) {
      checkErrorRatio(selectedImage)
        .then((hasError) => {
          setHasRatioError(hasError)
          hasError && formik?.setFieldError?.('mediaSocials', ratioError)
        })
        .catch(() => setHasRatioError(false))
      return
    }

    const handleRatioCheck = (ratioErrors) => {
      const hasImageInRatio = ratioErrors.some((error) => !error)

      if (hasImageInRatio) {
        setHasRatioError(false)
        previousSelectedImageIdRef.current = null
        formik?.setFieldError?.('mediaSocials', undefined)
        return
      } else if (images.length > 0) {
        setHasRatioError(true)
        formik?.setFieldError?.('mediaSocials', ratioError)
        previousSelectedImageIdRef.current = null
        return
      }
    }

    Promise.all(images.map((img) => checkErrorRatio(img)))
      .then(handleRatioCheck)
      .catch(() => setHasRatioError(false))
  }, [images, loadingCount, isPreview, formik?.values?.channels?.channelIds])

  useEffect(() => {
    const validation = validateImageCount(
      loadedCount,
      loadingCount,
      formatMessage,
    )

    if (
      !isPreview &&
      images?.length > displayLimit &&
      !hasUserClosedDialog &&
      loadingCount > 0 &&
      !validation.shouldBlock
    ) {
      setIsModalOpen(true)
    }
    if (images?.length <= displayLimit) {
      setHasUserClosedDialog(false)
    }
  }, [
    images?.length,
    displayLimit,
    isPreview,
    hasUserClosedDialog,
    loadingCount,
    loadedCount,
  ])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth
        if (width > 0) {
          setContainerWidth(width)
        }
      }
    }

    updateWidth()

    const resizeObserver = new ResizeObserver((entries) => {
      const width =
        entries[0]?.contentRect?.width || containerRef.current?.offsetWidth
      if (width > 0) {
        setContainerWidth(width)
      }
    })

    resizeObserver.observe(container)

    const timeoutId = setTimeout(() => {
      updateWidth()
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      resizeObserver.disconnect()
    }
  }, [images?.length])

  const handleUploadClick = () => {
    fileUpload.current?.click()
  }

  const handleFileChange = (event) => {
    const filesArray = getFilesFromEvent(event)
    if (!filesArray.length) return

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    const isValidType = R.flip(R.includes)(allowedTypes)
    const [validFiles, invalidFiles] = R.partition(
      R.compose(isValidType, R.prop('type')),
    )(filesArray)

    R.when(R.compose(R.gt(R.__, 0), R.length), () => {
      dispatch(
        MessageAlert.showWarning({
          message: formatMessage({
            id: 'compose.dialog.attachments.invalidFileType',
          }),
        }),
      )
      if (!validFiles.length) {
        R.when(
          R.prop('target'),
          R.tap((t) => (t.value = '')),
        )(event)
      }
    })(invalidFiles)

    if (!validFiles.length) return

    const currentImageCount = R.length(images) || 0
    const validation = validateImageCount(
      currentImageCount,
      validFiles.length,
      formatMessage,
    )

    if (validation.shouldBlock && !validation.isValid) {
      dispatch(
        MessageAlert.showError({
          message: validation.error?.message,
        }),
      )
      R.when(R.prop('target'), (t) => (t.value = ''))(event)
      return
    }

    R.forEach((warning) => {
      const alertFn = R.propEq('type', 'warning', warning)
        ? MessageAlert.showWarning
        : MessageAlert.showError
      dispatch(alertFn({ message: warning.message }))
    }, validation.warnings || [])

    const tempImages = R.addIndex(R.map)((fileItem, index) => {
      const uploadId = generateUploadId(fileItem.name)
      const isFirstImage = R.isEmpty(images) && index === 0
      return createTempImage(fileItem, uploadId, isFirstImage)
    })(validFiles)

    const updatedImages = [...images, ...tempImages]
    setValue(updatedImages)
    setTouched(true)

    const dataTransfer = R.reduce((dt, file) => {
      dt.items.add(file)
      return dt
    }, new DataTransfer())(validFiles)

    R.when(R.compose(R.gt(R.__, 0), R.length, R.prop('files')), (dt) =>
      uploadImage(
        dt.files,
        currentImageCount,
        null,
        updatedImages,
        createProgressHandlerFactory(setValue),
        createUploadIdMap(tempImages),
      ),
    )(dataTransfer)

    R.when(R.prop('target'), (t) => (t.value = ''))(event)
  }

  const handleDeleteImage = (imageToDelete) => {
    if (imageToDelete?.bodyImageId && onSyncedImageDelete) {
      onSyncedImageDelete(imageToDelete.bodyImageId)
      setTouched(true)
      return
    }
    const deleteResponse = deleteImage(imageToDelete, formValue)
    setValue(deleteResponse)
    setTouched(true)
  }

  const getImageId = (image) => {
    return image?.id || image?.mediaId || image?.bodyImageId
  }

  const handleSelectImage = useMemo(
    () =>
      debounce(async (imageToSelect) => {
        const currentSelected = R.find(R.prop('isSelected'), formValue)
        const currentSelectedId = getImageId(currentSelected)
        const imageToSelectId = getImageId(imageToSelect)
        const isCurrentlySelected =
          currentSelectedId &&
          imageToSelectId &&
          currentSelectedId === imageToSelectId

        const updatedImages = await selectImage(imageToSelect, formValue)
        setValue(updatedImages)
        setTouched(true)
        formik?.validateForm?.()

        dispatch(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: `compose.dialog.attachments.feature.${
                isCurrentlySelected ? 'deselected' : 'selected'
              }`,
            }),
          }),
        )
      }, 500),
    [
      formValue,
      selectImage,
      setValue,
      setTouched,
      dispatch,
      formatMessage,
      formik,
    ],
  )

  const handleDeleteAllClick = () => {
    setIsConfirmDeleteOpen(true)
  }

  const handleConfirmDeleteAll = () => {
    if (onDeleteAll) {
      onDeleteAll()
    }
    if (onSyncedImageDelete) {
      formValue
        .filter((img) => img.bodyImageId)
        .forEach((img) => onSyncedImageDelete(img.bodyImageId))
    }
    setValue([])
    setTouched(true)
    setIsConfirmDeleteOpen(false)
    setIsModalOpen(false)
  }

  useEffect(() => {
    if (meta.touched && image) {
      setValue((currentFormValue) =>
        replaceTempImage(currentFormValue || [], image),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image, meta.touched])

  useEffect(() => {
    if (isStateError && errorMessage) {
      dispatch(MessageAlert.showError({ message: errorMessage }))

      setValue((prevImages) => cleanupFailedUploads(prevImages))
      onCloseErrorDialog()
    }
  }, [isStateError, errorMessage, dispatch, onCloseErrorDialog, setValue])

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
        {((isPreview && images?.length > 0) || !isPreview) && (
          <div className="flex items-center gap-2 text-16 sm:text-18 font-medium text-blue-950">
            <label>
              <FormattedMessage id="compose.dialog.attachments.label" />
            </label>
            {images?.length > 0 && !isPreview && (
              <span className="text-14 sm:text-16 flex items-center gap-2">
                <span>{`(${loadedCount})`}</span>
                {loadingCount > 0 && (
                  <span className="text-primary/70 text-12 sm:text-14">
                    {loadingCount}{' '}
                    {formatMessage({
                      id: 'compose.dialog.attachments.uploading',
                    })}
                    {'...'}
                  </span>
                )}
              </span>
            )}
            {includes(formik?.values?.channels?.channelIds, INSTAGRAM_VALUE) &&
              !isPreview &&
              !loadingCount > 0 && <div>*</div>}
          </div>
        )}
        {!isPreview && (
          <>
            <div
              onClick={handleUploadClick}
              className="flex items-center justify-center gap-[8px] py-[8px] px-[16px] h-[36px] text-primary w-fit cursor-pointer border border-neutral-300 rounded-[6px] hover:bg-primary/10"
            >
              <FilePlus
                size={18}
                className="w-[18px] h-[18px] sm:w-[20px] sm:h-[20px]"
              />
              <FormattedMessage id="compose.dialog.attachments.add" />
            </div>
            <input
              type="file"
              ref={fileUpload}
              accept="image/png, image/jpg, image/jpeg, image/webp"
              className="hidden"
              multiple
              onClick={(event) => {
                event.target.value = ''
              }}
              onChange={handleFileChange}
            />
          </>
        )}

        {isPreview && images?.length > 3 && (
          <span className="text-14 text-primary/70">See more</span>
        )}
      </div>

      {includes(formik?.values?.channels?.channelIds, INSTAGRAM_VALUE) &&
        !isPreview &&
        !loadingCount > 0 &&
        (hasRatioError || !images?.length > 0) && (
          <div
            className={clsx(
              'py-4 sm:py-6 md:py-8 px-6 sm:px-8 md:px-10 my-2 sm:my-4 md:my-6 flex items-start sm:items-center gap-2 sm:gap-3 rounded-md text-12 sm:text-14',
              hasRatioError
                ? 'text-red-600 bg-red-200/40'
                : 'text-orange-600 bg-orange-200/40',
            )}
          >
            <Info
              size={14}
              className="w-[14px] h-[14px] sm:w-[16px] sm:h-[16px] mt-0.5 sm:mt-0 flex-shrink-0"
            />
            <span>
              {hasRatioError
                ? formatMessage({
                    id: 'compose.dialog.attachments.instagram.ratioError',
                  })
                : formatMessage({
                    id: 'compose.dialog.attachments.instagram.warning',
                  })}
            </span>
          </div>
        )}

      {images?.length == 0 && !isLoading && !isPreview && (
        <div className="flex items-center justify-center h-[80px] border border-primary/20 rounded-5 mt-8">
          <p className="text-14 font-medium text-primary/80">
            <FormattedMessage id="compose.dialog.attachments.emptyState" />
          </p>
        </div>
      )}

      {(images?.length > 0 || (images?.length === 0 && isLoading)) && (
        <div
          ref={containerRef}
          className={clsx(
            'attachment-scroll-container flex flex-nowrap gap-[10px] py-4 sm:py-8 mb-4 sm:mb-8 overflow-x-hidden',
            isPreview && 'gap-3',
          )}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {R.addIndex(R.map)((image, index) => {
            const imageKey =
              R.prop('id', image) || R.prop('uploadId', image) || index
            const currentDisplayLimit = isPreview ? 3 : displayLimit

            return R.cond([
              [
                () =>
                  shouldShowMoreButton(
                    sortedImages,
                    index,
                    isPreview,
                    currentDisplayLimit,
                  ),
                () => (
                  <div
                    key={`more-${index}`}
                    onClick={() => setIsModalOpen(true)}
                    data-testid="see-more-button"
                    className="flex-shrink-0 flex items-center justify-center rounded-[8px] w-[165px] min-h-[148px] bg-grey-400 cursor-pointer text-xs sm:text-sm px-4 py-3 sm:py-4 md:py-6 text-white hover:bg-grey-500 transition-colors"
                  >
                    {`+${getRemainingImagesCount(sortedImages, currentDisplayLimit)} ${formatMessage({ id: 'compose.dialog.attachments.seeMore' })}`}
                  </div>
                ),
              ],
              [
                () => shouldShowImage(index, isPreview, currentDisplayLimit),
                () => (
                  <AttachmentImage
                    key={`image-${index}-${imageKey}`}
                    image={image}
                    index={index}
                    isPreview={isPreview}
                    handleDeleteImage={handleDeleteImage}
                    handleSelectImage={handleSelectImage}
                    displayLimit={displayLimit}
                    disableSelect={images.length <= 1}
                  />
                ),
              ],
              [R.T, R.always(null)],
            ])()
          })(sortedImages)}
        </div>
      )}

      {/* Image Gallery Modal */}
      <ImageGalleryDialog
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setHasUserClosedDialog(true)
        }}
        images={sortedImages}
        onSelectImage={handleSelectImage}
        onDeleteImage={handleDeleteImage}
        onDeleteAll={handleDeleteAllClick}
        loadedCount={loadedCount}
        loadingCount={loadingCount}
      />

      <ConfirmationDialog
        open={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        title={
          <div className="text-[18px] font-bold text-[#1B3560]">
            {formatMessage({
              id: 'compose.dialog.attachments.deleteAll.dialog.title',
            })}
          </div>
        }
        body={formatMessage({
          id: 'compose.dialog.attachments.deleteAll.dialog.body',
        })}
        CancelButton={
          <OutlinedButtons
            color={CLOSE}
            onClick={() => setIsConfirmDeleteOpen(false)}
            id="close-image-gallery-button"
          >
            <FormattedMessage id="action.close" />
          </OutlinedButtons>
        }
        ConfirmButton={
          <ContainedButton
            color={SECONDARY}
            onClick={handleConfirmDeleteAll}
            className="bg-red-500 text-white"
          >
            <FormattedMessage id="action.delete" />
          </ContainedButton>
        }
      />
    </>
  )
}
